import type { ReactNode } from "react";
import { Link } from "react-router";

import {
  thumbSize,
  useFirstTradeImage,
  useMediaSrcSet,
  useMediaUrl,
  type TradeListItem,
} from "../api/hooks.ts";
import type { Note } from "../api/notes.ts";
import {
  badgeOf,
  formatPips,
  formatR,
  formatSignedPips,
  formatTime,
  labelOutcome,
  labelStatus,
  toneOf,
  formatLot,
} from "../domain/format.ts";
import { Icon } from "./Icon.tsx";

/**
 * Las tarjetas compactas de la plantilla (`.mini`). Viven aqui y no dentro de la
 * bitacora porque el dia del calendario pinta exactamente las mismas: dos copias
 * del mismo marcado se separan a la tercera correccion.
 */

/**
 * La tarjeta de una operacion es un POST tambien en compacto (ADR 0007): la
 * primera captura arriba y ancha, y debajo la linea de pips —riesgo, objetivo,
 * resultado— con el R al lado.
 */
export function MiniTrade(props: { trade: TradeListItem }): ReactNode {
  const { trade } = props;

  return (
    <Link className={`mini ${miniClass(trade)}`} to={`/trades/${trade.id}`}>
      <MiniThumb tradeId={trade.id} />
      <div className="mini-h">
        <span className="sym">{trade.instrumentCode}</span>
        <span className={trade.direction === "buy" ? "dir long" : "dir short"}>
          {trade.direction === "buy" ? "Long" : "Short"}
        </span>
        <span className="mini-time">{labelStatus(trade.status)}</span>
      </div>
      <b>{trade.entryCode}</b>
      <span className="mini-pips">
        <span title="Pips de riesgo">SL {formatPips(trade.plannedRiskPips)}</span>
        <span title="Pips de objetivo">TP {formatPips(trade.plannedRewardPips)}</span>
        {trade.resultPips !== null && (
          <span className={toneOf(trade.resultPips)} title="Pips ganados o perdidos">
            {formatSignedPips(trade.resultPips)} pips
          </span>
        )}
        <span className={toneOf(trade.rMultiple)} title="R multiplo">
          {formatR(trade.rMultiple)}
        </span>
        {/* Sin lotaje no se pinta nada: un guion donde no hay dato se lee como
            un cero, y cero lotes no es una operacion pequena, es ninguna. */}
        {trade.lotSize !== null && (
          <span className="mini-lot" title="Lotaje">
            {formatLot(trade.lotSize)} lotes
          </span>
        )}
      </span>
      <div className="mini-f">
        <span className={`badge ${badgeOf(trade.outcome)}`}>
          {trade.outcome === null ? "Abierta" : labelOutcome(trade.outcome)}
        </span>
        {trade.hasBrokenMandatoryRule && <span className="tag bad">Regla rota</span>}
        {trade.pipsDisagree && <span className="tag warn">Pips por revisar</span>}
      </div>
    </Link>
  );
}

/**
 * El hueco de la miniatura de la tarjeta compacta (`.mini-thumb`), con la
 * PRIMERA captura de la operacion. En modo ramas, donde todo son tarjetas de
 * dos lineas, el grafico es lo unico que distingue una entrada de otra de un
 * vistazo.
 *
 * Sin capturas no queda ningun agujero: el hueco no se pinta y la tarjeta se
 * cierra sobre su texto. Mientras se sabe, tampoco: reservar el sitio dejaria
 * un rectangulo vacio en las operaciones sin capturas, que son la mayoria.
 *
 * El `width`/`height` del `<img>` es lo que evita que la rejilla salte cuando la
 * imagen termina de cargar, y `loading="lazy"` lo que evita pedir las que estan
 * fuera de la pantalla.
 */
function MiniThumb(props: { tradeId: string }): ReactNode {
  const { image } = useFirstTradeImage(props.tradeId);
  const mediaUrl = useMediaUrl();
  const mediaSrcSet = useMediaSrcSet();

  if (image === undefined) return null;

  const size = thumbSize(image);

  return (
    <span className="mini-thumb">
      {/* Las DOS variantes, que elija el navegador. Con `thumb` fija se veia
          borrosa en el telefono: el hueco mide unos 295 px de lado, pero en una
          pantalla de densidad 3 eso son casi 900 px reales, y la pequena solo
          tiene 400. Es la mitad del fallo que el propietario vio "en el timeline
          o ramas"; la otra mitad estaba en la tarjeta del feed. */}
      <img
        /* Reserva, como en la tarjeta del feed: quien entiende `srcset` ignora
           este `src` y no lo descarga. */
        src={mediaUrl(image.contentHash, "view")}
        srcSet={mediaSrcSet(image.contentHash, image.width)}
        sizes="(max-width: 760px) calc(100vw - 80px), 300px"
        width={size.width}
        height={size.height}
        loading="lazy"
        decoding="async"
        alt=""
      />
    </span>
  );
}

/**
 * La nota. Con `onOpen` es un boton de verdad, con su clic y su teclado
 * nativos; sin el es una tarjeta que solo se lee, y entonces no finge ser
 * pulsable: un cursor de mano sobre algo que no responde es una promesa rota.
 *
 * Por dentro solo lleva contenido de frase, porque un <div> o un <p> dentro de
 * un <button> es marcado invalido. Los <span> se pintan igual: la tarjeta es un
 * contenedor flex.
 */
export function MiniNote(props: {
  note: Note;
  timeZone: string;
  onOpen?: (() => void) | undefined;
}): ReactNode {
  const body = (
    <>
      <span className="mini-h">
        <span className="ttag">
          <Icon name="msg" />
          Nota
        </span>
        <span className="mini-time">{formatTime(props.note.postedAtUtc, props.timeZone)}</span>
      </span>
      <span className="tw-line">{props.note.text}</span>
    </>
  );

  if (props.onOpen === undefined) {
    return <div className="mini tw ro">{body}</div>;
  }

  return (
    <button type="button" className="mini tw" onClick={props.onOpen}>
      {body}
    </button>
  );
}

/**
 * La regla innegociable rota manda sobre el resultado: el ambar mide
 * disciplina, y eso pesa mas que si la operacion gano (ADR 0003).
 */
function miniClass(trade: TradeListItem): string {
  if (trade.hasBrokenMandatoryRule) return "breach";
  if (trade.outcome === "win") return "win";
  if (trade.outcome === "loss") return "loss";

  return "be";
}
